import { ApiError } from "@nabble/sdk";
import { Alert, Button, Field, PageHead, TextInput, useToast } from "@nabble/ui";
import { type FormEvent, useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { errorMessage } from "../../../errors";
import { fetchOrganization, renameOrganization } from "../../../api/organizations";
import { useManagedOrganization } from "../../../hooks/useManagedOrganization";

/** The organization's own details (for now, its name). */
export default function OrganizationDetailsPage(): React.JSX.Element {
  const { show } = useToast();
  const { refreshUser } = useAuth();
  const { organizationId, picker } = useManagedOrganization();
  const [name, setName] = useState("");
  const [saved, setSaved] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    fetchOrganization(organizationId)
      .then((o) => {
        setName(o.name);
        setSaved(o.name);
      })
      .catch((err: unknown) => setError(errorMessage(err, "We couldn't load the organization.")))
      .finally(() => setLoading(false));
  }, [organizationId]);

  async function submit(e?: FormEvent): Promise<void> {
    e?.preventDefault();
    if (!organizationId || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await renameOrganization(organizationId, name.trim());
      setSaved(name.trim());
      show({ title: "Organization renamed", text: name.trim(), tone: "success" });
      await refreshUser();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "organization_name_taken"
          ? "Another organization already uses that name."
          : errorMessage(err, "We couldn't save the name."),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="nb-stack">
      <PageHead titleHidden title="Details" />
      {picker}
      <section className="nb-card nb-card-pad">
        <form className="nb-stack" onSubmit={(e) => void submit(e)} noValidate style={{ maxWidth: 480 }}>
          <Field label="Name" help="Everyone in the organization sees this name in the menu and in their lists.">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={loading} autoComplete="off" />
          </Field>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <div>
            <Button label="Save" type="submit" loading={saving} disabled={loading || !name.trim() || name.trim() === saved} />
          </div>
        </form>
      </section>
    </div>
  );
}
